import React, { useState, useEffect, useCallback, useRef } from 'react';
import { SearchBar } from '../components/Papers/SearchBar';
import { FilterSection } from '../components/Papers/FilterSection';
import { PaperCard } from '../components/Papers/PaperCard';
import { DateFilter } from '../components/Papers/DateFilter';
import { MobileFilterToggle } from '../components/Papers/MobileFilterToggle';
import { papersService } from '../services/papersService';
import { debounce } from 'lodash';

export const Papers = () => {
    const [papers, setPapers] = useState([]);
    const [filters, setFilters] = useState({
        authors: [],
        categories: [],
        affiliations: [],
        sources: []
    });
    const [searchQuery, setSearchQuery] = useState('');
    const [appliedSearchQuery, setAppliedSearchQuery] = useState('');
    const [filterOptions, setFilterOptions] = useState({
        authors: [],
        categories: [],
        affiliations: [],
        sources: []
    });
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [dateFilter, setDateFilter] = useState({ type: 'all', range: { from: '', to: '' } });

    // 필터 사이드바 토글 상태
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterRef = useRef(null);

    // 활성화된 필터 수 계산
    const activeFiltersCount =
        filters.authors.length +
        filters.categories.length +
        filters.affiliations.length +
        filters.sources.length +
        (dateFilter.type !== 'all' ? 1 : 0);

    // 필터 사이드바 외부 클릭 감지
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (filterRef.current && !filterRef.current.contains(event.target) && isFilterOpen) {
                setIsFilterOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isFilterOpen]);

    // 화면 크기에 따라 필터 상태 조절
    useEffect(() => {
        let lastWidth = window.innerWidth;

        const handleResize = () => {
            const newWidth = window.innerWidth;

            // 너비만 변경됐을 때만 필터 상태 변경 (높이 변화는 키보드 때문일 가능성이 높음)
            if (newWidth !== lastWidth) {
                if (newWidth >= 768) {
                    setIsFilterOpen(true);
                } else if (isFilterOpen) {
                    // 작은 화면에서는 현재 상태 유지 (닫지 않음)
                    // 너비가 변할 때만 상태를 변경
                    setIsFilterOpen(false);
                }

                lastWidth = newWidth;
            }
        };

        // 초기 로드 시 설정
        handleResize();

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [isFilterOpen]); // isFilterOpen 의존성 추가

    // fetchPapers를 useCallback으로 감싸서 의존성 문제 해결
    const fetchPapers = useCallback(async (searchTerm) => {
        try {
            setLoading(true);
            const params = {
                page,
                limit: 20,
                keywords: searchTerm,
                authors: filters.authors.length > 0 ? filters.authors.join(';') : undefined,
                categories: filters.categories.length > 0 ? filters.categories.join(';') : undefined,
                affiliations: filters.affiliations.length > 0 ? filters.affiliations.join(';') : undefined,
                source: filters.sources.length > 0 ? filters.sources.join(';') : undefined,
                date_from: dateFilter.range.from || undefined,
                date_to: dateFilter.range.to || undefined
            };

            const data = await papersService.getPapers(params);
            if (!loading) { // 새로운 검색이 시작되지 않았을 때만 상태 업데이트
                setPapers(data.papers);
                setAppliedSearchQuery(searchTerm);
                setTotalCount(data.total_count);
                setFilterOptions(data.filter_options);
            }
        } catch (error) {
            console.error('Error in fetchPapers:', error);
        } finally {
            setLoading(false);
        }
    }, [page, filters, searchQuery, dateFilter]);

    useEffect(() => {
        if (!loading) { // 검색 중이 아닐 때만 실행
            setLoading(true);
            fetchPapers(searchQuery);
        }
    }, [page, filters, dateFilter]);
    const renderPagination = () => {
        const totalPages = Math.ceil(totalCount / 20);
        const currentPage = page;
        let pages = [];
        // 화면 크기에 따라 delta 값 조정
        const delta = window.innerWidth < 640 ? 1 : 2;

        for (let i = 1; i <= totalPages; i++) {
            if (
                i === 1 ||
                i === totalPages ||
                (i >= currentPage - delta && i <= currentPage + delta)
            ) {
                pages.push(i);
            } else if (pages[pages.length - 1] !== '...') {
                pages.push('...');
            }
        }

        return (
            <div className="flex justify-center gap-1 sm:gap-2 items-center overflow-x-auto max-w-full px-2">
                <button
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-2 sm:px-3 py-1 sm:py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm sm:text-base flex-shrink-0"
                >
                    ←
                </button>

                {pages.map((pageNum, idx) => (
                    pageNum === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-1 sm:px-2 flex-shrink-0">...</span>
                    ) : (
                        <button
                            key={pageNum}
                            onClick={() => setPage(pageNum)}
                            className={`px-2 sm:px-3 py-1 sm:py-2 border rounded min-w-[32px] sm:min-w-[40px] text-sm sm:text-base flex-shrink-0 ${currentPage === pageNum
                                ? 'bg-primary-600 text-white'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            {pageNum}
                        </button>
                    )
                ))}

                <button
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-2 sm:px-3 py-1 sm:py-2 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 text-sm sm:text-base flex-shrink-0"
                >
                    →
                </button>
            </div>
        );
    };

    // debounced search 함수 생성
    const debouncedSearch = useCallback(
        debounce((term) => {
            setLoading(true);
            setPage(1);
            fetchPapers(term);
        }, 1000),
        [] // 의존성 제거
    );
    // cleanup을 위한 useEffect
    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    // SearchBar의 onChange 핸들러 수정
    const handleSearchChange = (value) => {
        setSearchQuery(value);
        debouncedSearch(value);
    };

    // 필터 토글 핸들러
    const toggleFilter = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex gap-8 mb-24 relative">
                {/* Sidebar - 모바일에서는 오버레이로 */}
                <div
                    ref={filterRef}
                    className={`${isFilterOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
                        } transition-transform duration-300 ease-in-out fixed md:relative top-0 left-0 z-20 md:z-0 h-full md:h-auto w-64 md:w-64 flex-shrink-0 bg-white shadow-lg md:shadow-sm overflow-y-auto pb-24 md:pb-0`}
                >
                    <div className="bg-white rounded-lg p-6">
                        <div className="md:hidden flex justify-between items-center mb-4">
                            <h2 className="text-lg font-medium">Filters</h2>
                            <button
                                onClick={toggleFilter}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <span className="sr-only">Close filter</span>
                                ✕
                            </button>
                        </div>

                        <FilterSection
                            title="Categories"
                            items={filterOptions.categories || []}
                            selected={filters.categories}
                            onChange={(newCategories) =>
                                setFilters(prev => ({ ...prev, categories: newCategories }))
                            }
                        />
                        <FilterSection
                            title="Authors"
                            // authors는 컴포넌트 내부에서 자체적으로 데이터를 가져옵니다
                            items={[]} // 빈 배열을 전달하여 컴포넌트가 자체적으로 로딩하도록 함
                            selected={filters.authors}
                            onChange={(newAuthors) =>
                                setFilters(prev => ({ ...prev, authors: newAuthors }))
                            }
                        />
                        <FilterSection
                            title="Affiliations"
                            items={filterOptions.affiliations || []}
                            selected={filters.affiliations}
                            onChange={(newAffiliations) =>
                                setFilters(prev => ({ ...prev, affiliations: newAffiliations }))
                            }
                        />
                        <FilterSection
                            title="Sources"
                            items={filterOptions.sources || []}
                            selected={filters.sources}
                            onChange={(newSources) =>
                                setFilters(prev => ({ ...prev, sources: newSources }))
                            }
                        />
                        <DateFilter
                            onFilterChange={(type, range) => {
                                setDateFilter({ type, range });
                                setPage(1); // 필터 변경시 첫 페이지로 이동
                            }}
                        />
                    </div>
                </div>

                {/* 필터가 열려있을 때 오버레이 (모바일 전용) */}
                {isFilterOpen && (
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 z-10 md:hidden"
                        onClick={toggleFilter}
                    />
                )}

                {/* 모바일 필터 토글 버튼 */}
                <MobileFilterToggle
                    isOpen={isFilterOpen}
                    onClick={toggleFilter}
                    activeFiltersCount={activeFiltersCount}
                />

                {/* Main Content */}
                <div className="flex-1 w-full">
                    <div className="mb-6">
                        <SearchBar
                            value={searchQuery}
                            onChange={handleSearchChange}
                            onSearch={() => debouncedSearch(searchQuery)}
                        />
                    </div>

                    {/* 적용된 필터 요약 (모바일용) */}
                    {activeFiltersCount > 0 && !isFilterOpen && (
                        <div className="flex flex-wrap gap-2 mb-4 md:hidden">
                            <span className="text-sm text-gray-500">Applied filters:</span>
                            {filters.categories.length > 0 && (
                                <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                    {filters.categories.length} categories
                                </span>
                            )}
                            {filters.authors.length > 0 && (
                                <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                    {filters.authors.length} authors
                                </span>
                            )}
                            {filters.affiliations.length > 0 && (
                                <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                    {filters.affiliations.length} affiliations
                                </span>
                            )}
                            {filters.sources.length > 0 && (
                                <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                    {filters.sources.length} sources
                                </span>
                            )}
                            {dateFilter.type !== 'all' && (
                                <span className="text-xs bg-primary-100 text-primary-800 px-2 py-1 rounded-full">
                                    Date: {dateFilter.type}
                                </span>
                            )}
                        </div>
                    )}

                    {loading ? (
                        <div className="text-center py-8">Loading...</div>
                    ) : papers.length === 0 ? (
                        <div className="text-center py-8">No papers found</div>
                    ) : (
                        <>
                            <div className="mb-4">
                                <p className="text-sm text-gray-500">
                                    Showing {((page - 1) * 20) + 1}-{Math.min(page * 20, totalCount)} of {totalCount} papers (sorted by most recent)
                                </p>
                            </div>
                            <div className="space-y-4">
                                {papers.map((paper) => (
                                    <PaperCard
                                        key={paper.paper_id}
                                        paper={paper}
                                        searchQuery={appliedSearchQuery}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>
            {/* Fixed Pagination */}
            {!loading && papers.length > 0 && (
                <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t shadow-lg">
                    <div className="max-w-7xl mx-auto">
                        {renderPagination()}
                    </div>
                </div>
            )}
        </div>
    );
};